import React, { useEffect, useState } from "react";
import SwapInboxCard from "./SwapInboxCard";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../config";

/**
 * Normalise ANY inbox response shape into an array.
 * Supports:
 * - { requests: [...] }
 * - { inbox: [...] }
 * - { data: [...] }
 * - [...]
 */
function normaliseToArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload.requests)) return payload.requests;
  if (Array.isArray(payload.inbox)) return payload.inbox;
  if (Array.isArray(payload.data)) return payload.data;

  // sometimes APIs return { success:true, rows:[...] }
  if (Array.isArray(payload.rows)) return payload.rows;

  return [];
}

const SwapInbox = () => {
  const [requests, setRequests] = useState([]);
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();

  const handleBack = () => {
  if (window.history.length > 1) {
    navigate(-1);
  } else {
    navigate("/dashboard");
  }
};


  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchInbox = async () => {
      try {
        setMsg("");

        if (!user?.id || !token) {
          setRequests([]);
          setMsg("You are not logged in (missing user/token). Please log in again.");
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };

        const [mowingRes, cleaningRes, poolCareRes] = await Promise.allSettled([
  fetch(`${API_BASE_URL}/api/swaps/inbox/${user.id}`, { headers }),
  fetch(`${API_BASE_URL}/api/cleaning/swaps/inbox`, { headers }),
  fetch(`${API_BASE_URL}/api/poolcare/swaps/inbox`, { headers }) // ✅ NEW
]);



        const all = [];

        // MOWING
        if (mowingRes.status === "fulfilled") {
          const res = mowingRes.value;
          if (res.ok) {
            const json = await res.json().catch(() => null);
            const arr = normaliseToArray(json).map((r) => ({
              ...r,
              category: "mowing",
            }));
            all.push(...arr);
          } else {
            // don’t silently fail
            setMsg((prev) => prev || `Mowing inbox returned ${res.status}`);
          }
        } else {
          setMsg((prev) => prev || "Mowing inbox failed to load");
        }

        // CLEANING
        if (cleaningRes.status === "fulfilled") {
          const res = cleaningRes.value;
          if (res.ok) {
            const json = await res.json().catch(() => null);
            const arr = normaliseToArray(json).map((r) => ({
              ...r,
              category: "cleaning",
            }));
            all.push(...arr);
          } else {
            setMsg((prev) => prev || `Cleaning inbox returned ${res.status}`);
          }
        } else {
          setMsg((prev) => prev || "Cleaning inbox failed to load");
        }
        // POOL CARE
if (poolCareRes.status === "fulfilled") {
  const res = poolCareRes.value;
  if (res.ok) {
    const json = await res.json().catch(() => null);
    const arr = normaliseToArray(json).map((r) => ({
      ...r,
      category: "pool_care",
    }));
    all.push(...arr);
  } else {
    setMsg((prev) => prev || `Pool care inbox returned ${res.status}`);
  }
} else {
  setMsg((prev) => prev || "Pool care inbox failed to load");
}


        // Sort newest first if created_at exists
        all.sort((a, b) => {
          const da = a.created_at ? new Date(a.created_at).getTime() : 0;
          const db = b.created_at ? new Date(b.created_at).getTime() : 0;
          return db - da;
        });

        setRequests(all);

        // If both returned OK but still nothing, show a direct message
        if (all.length === 0 && !msg) {
          setMsg("No incoming swap requests for this account.");
        }
      } catch (err) {
        console.error("❌ Inbox fetch failed:", err);
        setRequests([]);
        setMsg("Inbox failed to load (unexpected error).");
      }
    };

    fetchInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  /* ============================
     ACCEPT HANDLER
  ============================ */
const handleAccept = async (request) => {
  try {
    const endpoint =
      request.category === "cleaning"
        ? `${API_BASE_URL}/api/cleaning/swaps/accept`
         : request.category === "pool_care"
    ? `${API_BASE_URL}/api/poolcare/swaps/accept`
        : `${API_BASE_URL}/api/swaps/accept`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ requestId: request.id }),
    });

    if (!res.ok) {
      throw new Error(`Accept failed (${res.status})`);
    }

    const data = await res.json();

    if (!data.success) {
      alert(data.message || "Accept failed");
      return;
    }

  // Pool Care already returns Stripe URL
if (data.url) {
  window.location.href = data.url;
  return;
}
// ✅ POOL CARE: go to correct deal page
if ((request.category === "pool_care" || request.category === "pool_care") && data.dealId) {
  navigate(`/poolcare/swaps/deal/${data.dealId}`);
  return;
}

// 🔥 CLEANING: force immediate payment
if (request.category === "cleaning" && data.dealId) {
  navigate(`/cleaning/swaps/pay/${data.dealId}`);
  return;
}

// Mowing fallback only
navigate(`/swap-deal/${data.dealId}`);

  } catch (err) {
    console.error("❌ Accept error:", err);
    alert("Accept failed. Please try again.");
  }
};


  /* ============================
     DECLINE HANDLER
  ============================ */
  const handleDecline = async (request) => {
  try {
    const endpoint =
      request.category === "cleaning"
        ? `${API_BASE_URL}/api/cleaning/swaps/decline`
        : request.category === "pool_care"
        ? `${API_BASE_URL}/api/poolcare/swaps/decline`
        : `${API_BASE_URL}/api/swaps/decline`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ requestId: request.id }),
    });

    if (!res.ok) {
      throw new Error(`Decline failed (${res.status})`);
    }

    const data = await res.json();

    if (!data.success) {
      throw new Error("Decline failed");
    }

    setRequests((prev) => prev.filter((r) => r.id !== request.id));
  } catch (err) {
    console.error("❌ Decline error:", err);
    alert("Decline failed. Please try again.");
  }
};


  return (
    <div style={{ padding: 20, minHeight: "100vh", background: "#f4f7f9" }}>
      <h1 style={{ textAlign: "center", color: "#2e7d32" }}>Swap Inbox</h1>

      <button
        onClick={handleBack}

        style={{
          marginBottom: 20,
          padding: "10px 20px",
          background: "#2e7d32",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        ← Back
      </button>

      {msg && (
        <div
          style={{
            background: "#fff3cd",
            border: "1px solid #ffeeba",
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {msg}
        </div>
      )}

      {requests.length === 0 ? (
        <p style={{ textAlign: "center" }}>No swap requests yet.</p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
       {requests.map((req) => (
  <SwapInboxCard
    key={`${req.category}-${req.id}`}
    request={req}
    user={user}
    onAccept={() => handleAccept(req)}
    onDecline={() => handleDecline(req)}
  />
))}


        </div>
      )}
    </div>
  );
};

export default SwapInbox;
